import { HTTP_URL, SOCKET_URL } from "../config/backend";
import { useState, useEffect, useRef, useCallback } from "react";

import QuizHost from "../games/quiz/QuizHost";
import DrawingHost from "../games/drawing/DrawingHost";
import TruthDareHost from "../games/truthdare/TruthDareHost";
import CrocodileHost from "../games/crocodile/CrocodileHost";
import QuiplashHost from "../games/quiplash/QuiplashHost";
import FibbageHost from "../games/fibbage/FibbageHost";
import TapraceHost from "../games/taprace/TapraceHost";
import StroopHost from "../games/stroop/StroopHost";
import FlappyHost from "../games/flappy/FlappyHost";
import DictatorHost from "../games/dictator/DictatorHost";
import TwoTruthsHost from "../games/twotruths/TwoTruthsHost";
import VotingHost from "../games/voting/VotingHost";
import MusicHost from "../games/music/MusicHost";
import CarsHost from "../games/cars/CarsHost";
import PuzzleMusicHost from "../games/music/PuzzleMusicHost";
import { log } from "../hooks/useLogger";
import useTTS from "../hooks/useTTS";
import EmojiMovieHost from "../games/emojimovie/EmojiMovieHost";
import FactOrFakeHost from "../games/factorfake/FactOrFakeHost";
import BumperHost from "../games/bumper/BumperHost";
import WheelWordHost from "../games/wheelword/WheelWordHost";
import SniperHost from "../games/sniper/SniperHost";
import JeopardyHost from "../games/jeopardy/JeopardyHost";
import ReactionHost from "../games/reaction/ReactionHost";
import SimonHost from "../games/simon/SimonHost";
import QuestHost from "../games/quest/QuestHost";
import CircleTimer from "../shared/CircleTimer";
import { Spinner, ProgressBar, GuessFeed } from "../shared/ui";
import Crewmate from "../components/Crewmate";
import { useParams, useNavigate } from "react-router-dom";
import { io } from "socket.io-client";
import { Users, RotateCcw, Trophy, Crown, Palette, Brain, Vote, CheckCircle, XCircle, Flame, Smile, SkipForward, Music, Zap, MessageSquare, Flag, Film, HelpCircle, Star, Bird, Eye, Crosshair, Search, Laugh, Car, BookOpen } from "lucide-react";
import ScreenplayHost from "../games/screenplay/ScreenplayHost";
import SpyfallHost from "../games/spyfall/SpyfallHost";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { useSound } from "../hooks/useSound";

const BACKEND_URL = SOCKET_URL;

// ─── SVG icon helpers ────────────────────────────────────────────────────────
function SoundOn({sz}){return<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>}
function SoundOff({sz}){return<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>}
function MusicOn({sz}){return<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
function MusicOff({sz}){return<svg width={sz} height={sz} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"/><line x1="1" y1="1" x2="23" y2="23"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>}
function PlayIcon({sz=28}){return<svg width={sz} height={sz} viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>}

// ─── TV detection ────────────────────────────────────────────────────────────
function useIsTV(){
  const[v,s]=useState(()=>window.innerWidth>=1280);
  useEffect(()=>{const h=()=>s(window.innerWidth>=1280);window.addEventListener("resize",h);return()=>window.removeEventListener("resize",h)},[]);
  return v;
}

const GAME_RULES={
  quiz:"Каждый раунд — вопрос с 4 вариантами. Отвечай быстрее — получай больше очков.",
  drawing:"Один рисует слово, остальные угадывают. Кто угадал первым — больше очков.",
  voting:"Вопрос «Кто из вас…?» — все голосуют. Больше голосов = больше очков.",
  truthdare:"По очереди: Правда или Действие. Можно пропустить за -25 очков.",
  crocodile:"Один объясняет жестами без слов, остальные угадывают.",
  music:"Играет фрагмент — выбери правильный вариант из 4. Скорость = очки.",
  bumper:"8 раундов на разных картах. Тарань соперников, набивай урон — это и есть очки.",
  durak:"Атакуй, отбивайся. Сбросил все карты — не дурак. Последний — ДУРАК!",
  uno:"Сбрось все карты первым! Skip, Reverse, +2, Wild, +4. Забыл УНО — штраф!",
  quiplash:"Придумай смешной ответ на фразу. Потом все голосуют — 100 очков за каждый голос!",
  fibbage:"Необычный факт с пропуском — придумай ложь. +500 за угадывание правды, +300 за каждого обманутого!",
};
const GNAMES={quiz:"Викторина",cars:"Угадай марку",quiz_cinema:"Кино-викторина",quiz_18:"Викторина 18+",quiz_movies:"Угадай фильм",drawing:"Рисовалка",voting:"Голосование",truthdare:"Правда или Действие",crocodile:"Крокодил",music:"Угадай мелодию",reverse_music:"Музыка наоборот",fast_music:"Музыка на скорости",puzzle_music:"Музыкальный пазл",emoji_movie:"Фильм по эмодзи",fact_or_fake:"Правда или фейк",wheelword:"Поле чудес",flappy:"Птичий забег",bumper:"Бамперные машинки",quiplash:"Закончи фразу",taprace:"Гонка на тапах",dictator:"Диктатор",two_truths:"2 правды 1 ложь",stroop:"Не верь словам",sniper:"Снайпер",simon:"Повтори комбо",quest:"Квест",fibbage:"Фиббедж",jeopardy:"Своя игра",reaction:"Реакция",screenplay:"Безумный сценарист",lexicon:"Значения слов",spyfall:"Шпион"};
const GICONS={quiz:Brain,cars:Car,quiz_cinema:Brain,quiz_18:Brain,quiz_movies:Film,drawing:Palette,voting:Vote,truthdare:Flame,crocodile:Smile,music:Music,reverse_music:Music,fast_music:Music,puzzle_music:Music,emoji_movie:Film,fact_or_fake:HelpCircle,wheelword:Star,flappy:Bird,bumper:Zap,quiplash:MessageSquare,taprace:Flag,dictator:Crown,two_truths:Laugh,stroop:Eye,sniper:Crosshair,simon:Zap,quest:Search,fibbage:Laugh,jeopardy:Star,reaction:Zap,screenplay:Film,lexicon:BookOpen,spyfall:Search};
const GCOLORS={quiz:"#ff3cac",cars:"#FF6B00",quiz_cinema:"#E040FB",quiz_18:"#FF1744",quiz_movies:"#E040FB",drawing:"#00f5ff",voting:"#ffe600",truthdare:"#ff8c00",crocodile:"#3dffa0",music:"#9b5de5",reverse_music:"#7b3dd5",fast_music:"#FFD600",puzzle_music:"#C77DFF",emoji_movie:"#FF6B35",fact_or_fake:"#00C9FF",wheelword:"#FF6B6B",flappy:"#6BCB77",bumper:"#ff3355",quiplash:"#00DDFF",taprace:"#ff8c00",dictator:"#FFD700",two_truths:"#B24BF3",stroop:"#A0FF00",sniper:"#ff3355",simon:"#9b5de5",quest:"#4a90d9",fibbage:"#B24BF3",jeopardy:"#FFD700",reaction:"#00f5ff",screenplay:"#FF6B35",lexicon:"#4FC3F7",spyfall:"#7b3dd5"};
// FIX: correct min_players per game type (voting=3 needs 3 players, not 1)
const MIN_PLAYERS={quiz:1,cars:1,quiz_cinema:1,quiz_18:1,quiz_movies:1,drawing:2,voting:3,truthdare:2,crocodile:2,music:2,reverse_music:2,fast_music:2,puzzle_music:2,emoji_movie:2,fact_or_fake:2,wheelword:2,flappy:2,bumper:2,quiplash:3,taprace:2,dictator:3,two_truths:3,stroop:2,sniper:2,simon:2,quest:2,fibbage:2,jeopardy:2,reaction:2,screenplay:3,lexicon:1,spyfall:4};
const MAX_PLAYERS={quiz:8,cars:8,quiz_cinema:8,quiz_18:8,quiz_movies:8,drawing:8,voting:8,truthdare:8,crocodile:8,music:8,reverse_music:8,fast_music:8,puzzle_music:8,emoji_movie:8,fact_or_fake:8,wheelword:8,flappy:8,bumper:6,quiplash:8,taprace:8,dictator:10,two_truths:10,stroop:8,sniper:8,simon:8,quest:10,fibbage:8,jeopardy:8,reaction:8,screenplay:10,lexicon:8,spyfall:16};
const OPTS={colors:["#ff3355","#00aaff","#3dffa0","#ffe600"],labels:["A","B","C","D"]};

// ─── Main component ──────────────────────────────────────────────────────────
export default function HostLobby(){
  const{roomId}=useParams();const navigate=useNavigate();const tv=useIsTV();
  const socketRef=useRef(null); // use ref so event handlers always see latest socket
  const[players,setPlayers]=useState([]);
  const[scores,setScores]=useState({});
  const[gameState,setGameState]=useState("waiting");
  const[gameType,setGameType]=useState("quiz");
  // Socket listeners are registered once per room. Keep a synchronous ref so
  // reconnect/question handlers never capture the initial "quiz" value.
  const gameTypeRef=useRef("quiz");
  const[curRound,setCurRound]=useState(0);
  const[maxRounds,setMaxRounds]=useState(5);
  // Quiz
  const[quizQ,setQuizQ]=useState(null);const[quizOpts,setQuizOpts]=useState([]);
  const[carsLogoSlug,setCarsLogoSlug]=useState(null);const[carsOpts,setCarsOpts]=useState([]);const[carsResult,setCarsResult]=useState(null);
  const[answered,setAnswered]=useState([]);const[quizResult,setQuizResult]=useState(null);
  // Drawing
  const[drawer,setDrawer]=useState(null);const[drawHint,setDrawHint]=useState("");
  const[drawData,setDrawData]=useState(null);const[guesses,setGuesses]=useState([]);const[revealed,setRevealed]=useState(null);
  // Voting
  const[votingQ,setVotingQ]=useState(null);const[voted,setVoted]=useState([]);const[vResult,setVResult]=useState(null);
  // Truth or Dare
  const[tdPlayer,setTdPlayer]=useState(null);const[tdType,setTdType]=useState(null);
  const[tdContent,setTdContent]=useState(null);const[tdDone,setTdDone]=useState(false);const[tdSkipped,setTdSkipped]=useState(false);
  // Crocodile
  const[cExplainer,setCExplainer]=useState(null);const[cWord,setCWord]=useState("");
  const[cCat,setCCat]=useState("");const[cTime,setCTime]=useState(60);const[cMaxTime,setCMaxTime]=useState(60);
  const[cGuessedBy,setCGuessedBy]=useState([]);const[cGuesses,setCGuesses]=useState([]);const[cEnd,setCEnd]=useState(null);
  // Results
  const[finalScores,setFinalScores]=useState([]);const[winner,setWinner]=useState(null);const[gameEndData,setGameEndData]=useState(null);
  // Music
  const[musicOpts,setMusicOpts]=useState([]);const[musicAnswered,setMusicAnswered]=useState([]);
  const[puzzleSegments,setPuzzleSegments]=useState([]);
  const[musicResult,setMusicResult]=useState(null);const[musicTime,setMusicTime]=useState(30);
  const[musicHint,setMusicHint]=useState("");
  const[musicAudioUrl,setMusicAudioUrl]=useState("");const[musicCoverUrl,setMusicCoverUrl]=useState("");
  const[reverseProxyUrl,setReverseProxyUrl]=useState("");
  const[playbackRate,setPlaybackRate]=useState(1);
  // Emoji Movie
  const[emojiQ,setEmojiQ]=useState(null);const[emojiAnswered,setEmojiAnswered]=useState([]);const[emojiResult,setEmojiResult]=useState(null);const[emojiTime,setEmojiTime]=useState(20);
  // Fact or Fake
  const[fofFact,setFofFact]=useState("");const[fofAnswered,setFofAnswered]=useState([]);const[fofResult,setFofResult]=useState(null);const[fofTime,setFofTime]=useState(15);
  // flappy state
  const[flappyData,setFlappyData]=useState(null);
  const[flappyRoundEnd,setFlappyRoundEnd]=useState(null);
  const[flappyAlive,setFlappyAlive]=useState(new Set());
  const[flappyScores,setFlappyScores]=useState({});
  const flappyPositionsRef=useRef({});
  const setFlappyPositions = v => { flappyPositionsRef.current = typeof v==="function"?v(flappyPositionsRef.current):v; };

  // wheelword state
  const[wwWord,setWwWord]=useState(null);       // {mask, category, word_length}
  const[wwCurrentPlayer,setWwCurrentPlayer]=useState(null);
  const[wwPhase,setWwPhase]=useState("spin");   // spin | action | joker
  const[wwSpinResult,setWwSpinResult]=useState(null);  // {label,type,value,sector_idx}
  const[wwSpinResultVisible,setWwSpinResultVisible]=useState(false);
  const[wwSpinKey,setWwSpinKey]=useState(0); // инкрементируется только при spin_result, не при spin_reveal
  const[wwLetterResult,setWwLetterResult]=useState(null);
  const[wwWordResult,setWwWordResult]=useState(null);
  const[wwHint,setWwHint]=useState(null);
  const[wwRoundEnd,setWwRoundEnd]=useState(null);
  const[wwTick,setWwTick]=useState(60);
  // sniper state
  const[sniperTurn,setSniperTurn]=useState(null);
  const[sniperShot,setSniperShot]=useState(null);
  // jeopardy state
  const[jeopardyBoard,setJeopardyBoard]=useState(null);
  const[jeopardyResult,setJeopardyResult]=useState(null);
  const[jeopardyTimeout,setJeopardyTimeout]=useState(null);
  // reaction state
  const[reactionPhase,setReactionPhase]=useState("waiting");
  const[reactionSignal,setReactionSignal]=useState(null);
  const[reactionRoundEnd,setReactionRoundEnd]=useState(null);
  const[reactionEarly,setReactionEarly]=useState(null);
  const[reactionRound,setReactionRound]=useState(1);
  const[reactionMaxRounds,setReactionMaxRounds]=useState(10);

  const[simonData,setSimonData]=useState(null);
  const[questData,setQuestData]=useState(null);
  const[playlists,setPlaylists]=useState([]);
  const[selectedPlaylist,setSelectedPlaylist]=useState("music_world");
  const MUSIC_GAME_TYPES = new Set(["music","reverse_music","fast_music","puzzle_music"]);
  const[wwGuessedLetters,setWwGuessedLetters]=useState([]);
  const[wwRoundScores,setWwRoundScores]=useState({});
  const[wwEliminated,setWwEliminated]=useState([]);
  // Диктатор
  const[dictData,setDictData]=useState(null);       // {dictator, task, phase, round, max_rounds}
  const[dictLaw,setDictLaw]=useState(null);          // {law, dictator}
  const[dictVoteData,setDictVoteData]=useState(null);// {players, dictator, law}
  const[dictResult,setDictResult]=useState(null);    // {votes, vote_counts, most_accused, scores}
  // 2 правды 1 ложь
  const[ttData,setTtData]=useState(null);            // {subject, prompt, phase, round, max_rounds}
  const[ttVoteData,setTtVoteData]=useState(null);    // {subject, facts}
  const[ttVoteProgress,setTtVoteProgress]=useState(null); // {voted_count, total}
  const[ttResult,setTtResult]=useState(null);        // {facts, lie_index, votes, scores}
  // Stroop — Не верь словам
  const[stroopQuestion,setStroopQuestion]=useState(null);
  const[stroopResult,setStroopResult]=useState(null);
  const[stroopLives,setStroopLives]=useState({});
  // Bumper Cars (canvas uses ref to avoid 20fps re-renders)
  const bumperStateRef=useRef({players:[],timeLeft:60,scores:{}});
  const[quiplashPrompt,setQuiplashPrompt]=useState(null);
  const[spPhase,setSpPhase]=useState(null);
  const[spVoteCount,setSpVoteCount]=useState(0);
  const[spResult,setSpResult]=useState(null);
  const[spFinal,setSpFinal]=useState(null);
  // spyfall state
  const[sfPhase,setSfPhase]=useState(null);
  const[sfVoteData,setSfVoteData]=useState(null);
  const[sfResult,setSfResult]=useState(null);
  const[quiplashAnswerCount,setQuiplashAnswerCount]=useState(0);
  const[quiplashAnswerTotal,setQuiplashAnswerTotal]=useState(0);
  const[quiplashVoteData,setQuiplashVoteData]=useState(null);   // {prompt,answers}
  const[quiplashVoteCount,setQuiplashVoteCount]=useState(0);
  const[quiplashVoteTotal,setQuiplashVoteTotal]=useState(0);
  const[quiplashResult,setQuiplashResult]=useState(null);
  // Fibbage state
  const[fibbageQuestion,setFibbageQuestion]=useState(null);    // {prompt}
  const[fibbageLieCount,setFibbageLieCount]=useState(0);
  const[fibbageLieTotal,setFibbageLieTotal]=useState(0);
  const[fibbageVoteData,setFibbageVoteData]=useState(null);    // {prompt, options}
  const[fibbageVoteCount,setFibbageVoteCount]=useState(0);
  const[fibbageVoteTotal,setFibbageVoteTotal]=useState(0);
  const[fibbageResult,setFibbageResult]=useState(null);
  // Taprace state
  const[tapraceData,setTapraceData]=useState(null);       // {track, players, finish_pos}
  const[tapracePositions,setTapracePositions]=useState({}); // {name: pos}
  const[tapraceFinished,setTapraceFinished]=useState([]);   // names in finish order
  const[tapraceComboLevels,setTapraceComboLevels]=useState({});
  const[tapraceMultipliers,setTapraceMultipliers]=useState({});
  const[tapraceRoundResult,setTapraceRoundResult]=useState(null);
  const[tapraceEvent,setTapraceEvent]=useState(null);      // last event for flash
  const[bumperArena,setBumperArena]=useState(null);
  const[bumperElims,setBumperElims]=useState([]);
  const[bumperRoundResult,setBumperRoundResult]=useState(null);
  // Card games state

  const{soundEnabled,musicEnabled,playSound,toggleSound,toggleMusic,initAudio,stopMusic}=useSound();
  const{speak,stop:stopTTS}=useTTS();

  const resetRound=()=>{
    setQuizQ(null);setQuizOpts([]);setAnswered([]);setQuizResult(null);
    setDrawer(null);setDrawHint("");setDrawData(null);setGuesses([]);setRevealed(null);
    setVotingQ(null);setVoted([]);setVResult(null);
    setTdPlayer(null);setTdType(null);setTdContent(null);setTdDone(false);setTdSkipped(false);
    setCExplainer(null);setCWord("");setCCat("");setCTime(60);setCGuessedBy([]);setCGuesses([]);setCEnd(null);
    setMusicOpts([]);setMusicAnswered([]);setMusicResult(null);setMusicTime(30);setMusicHint("");setMusicAudioUrl("");setMusicCoverUrl("");setReverseProxyUrl("");setPlaybackRate(1);
    setEmojiQ(null);setEmojiAnswered([]);setEmojiResult(null);setEmojiTime(20);
    setFofFact("");setFofAnswered([]);setFofResult(null);setFofTime(15);
    setFlappyData(null);setFlappyRoundEnd(null);setFlappyAlive(new Set());setFlappyScores({});
    setWwWord(null);setWwCurrentPlayer(null);setWwPhase("spin");setWwSpinResult(null);setWwSpinResultVisible(false);
    setWwLetterResult(null);setWwWordResult(null);setWwHint(null);setWwRoundEnd(null);
    setSniperTurn(null);setSniperShot(null);
    setWwTick(60);setWwGuessedLetters([]);setWwRoundScores({});setWwEliminated([]);
    setDictData(null);setDictLaw(null);setDictVoteData(null);setDictResult(null);
    setTtData(null);setTtVoteData(null);setTtVoteProgress(null);setTtResult(null);
    setStroopQuestion(null);setStroopResult(null);setStroopLives({});
    bumperStateRef.current={players:[],timeLeft:60,scores:{}};setBumperArena(null);setBumperElims([]);setBumperRoundResult(null);setSpPhase(null);setSpVoteCount(0);setSpResult(null);setSpFinal(null);setQuiplashPrompt(null);setQuiplashAnswerCount(0);setQuiplashAnswerTotal(0);setQuiplashVoteData(null);setQuiplashVoteCount(0);setQuiplashVoteTotal(0);setQuiplashResult(null);setTapraceData(null);setTapracePositions({});setTapraceFinished([]);setTapraceComboLevels({});setTapraceMultipliers({});setTapraceRoundResult(null);setTapraceEvent(null);setSfPhase(null);setSfVoteData(null);setSfResult(null);
  };

  // FIX: Load room info from API so gameType/gameName is correct from the start
  useEffect(()=>{
    fetch(`${HTTP_URL}/api/rooms/${roomId}`)
      .then(r=>r.json())
      .then(d=>{if(d.game_type){gameTypeRef.current=d.game_type;setGameType(d.game_type);}})
      .catch(()=>{});
  },[roomId]);

  // Load playlists for music games
  useEffect(()=>{
    fetch(`${HTTP_URL}/api/music/playlists`)
      .then(r=>r.json())
      .then(d=>setPlaylists(d.playlists||[]))
      .catch(()=>{});
  },[]);

  useEffect(()=>{
    const s=io(BACKEND_URL,{
      transports:["polling","websocket"],
      tryAllTransports:true,
      path:"/api/socket.io",
      reconnection:true,
      reconnectionAttempts:Infinity,   // keep trying forever
      reconnectionDelay:1000,
      reconnectionDelayMax:5000,
    });
    socketRef.current=s;

    // FIX: emit host_join on EVERY connect (initial + reconnect)
    // Socket.IO fires "connect" both on first connect and after reconnection
    s.on("connect",()=>{
      const hostToken = sessionStorage.getItem(`host_token_${roomId}`) || "";
      s.emit("host_join",{room_id:roomId, host_token:hostToken});
    });

    // Heartbeat — сервер быстро узнает если хост закрыл вкладку
    const heartbeatInterval = setInterval(() => {
      if (s.connected) s.emit("host_heartbeat", { room_id: roomId });
    }, 2000);

    // Do not send host_leave from beforeunload/reload. Mobile browsers and
    // refreshes fire unload during short network transitions; the backend's
    // reconnect grace period is specifically designed to survive those.

    s.on("host_joined",()=>{});
    s.on("pro_usage_updated",(d)=>{
      if (Number.isFinite(d?.rooms_left)) {
        localStorage.setItem("neon_promo_rooms", String(d.rooms_left));
        if (d.rooms_left <= 0) localStorage.removeItem("neon_promo_code");
      }
    });

    // FIX: room_closed should only navigate away if it's actually closed
    // (not a temporary disconnect)
    s.on("room_closed",(d)=>{
      // If we're reconnecting this fires from stale state — only navigate on host_left
      if(d?.reason==="host_left"||d?.reason==="closed"){
        toast.error("Комната закрыта");
        navigate("/games");
      }
    });

    s.on("players_update",d=>{
      setPlayers(prev=>{
        if(d.players.length>prev.length) playSound("player_join");
        return d.players;
      });
      setScores(d.scores);
    });

    s.on("game_started",d=>{
      if(!d.rejoin) playSound("game_start");
      setGameState("playing");gameTypeRef.current=d.game_type;setGameType(d.game_type);
      if(!d.rejoin) resetRound();
      if(d.game_type==="bumper"&&!d.rejoin){setBumperArena({w:800,h:600,r:30});}
      if(["music","reverse_music","fast_music","puzzle_music"].includes(d.game_type)){stopMusic();}
    });
    s.on("game_restarted",d=>{stopMusic();setGameState("waiting");setScores(d.scores);resetRound();setCurRound(0);setFinalScores([]);setWinner(null);});
    s.on("bumper_round_start",d=>{
      setCurRound(d.round_num);setMaxRounds(d.total_rounds);
      setBumperArena({
        w:d.arena_w,h:d.arena_h,r:d.radius,
        obstacles:d.obstacles||[],platforms:d.platforms||[],
        isIslands:d.is_islands||false,bgColor:d.bg_color||"#080618",
        roundNum:d.round_num,totalRounds:d.total_rounds,
        roundName:d.round_name,roundEmoji:d.round_emoji,roundDesc:d.round_desc,
      });
      const initScores = d.cumulative_scores || {};
      bumperStateRef.current={players:d.players,timeLeft:d.time_seconds,scores:initScores};
      setScores(initScores);  // sync ArcadeScoreboard from round start
      setBumperElims([]);setBumperRoundResult(null);
    });
    s.on("bumper_round_end",d=>{
      setBumperRoundResult(d);
      if (d.scores) {
        bumperStateRef.current = {...bumperStateRef.current, scores: d.scores};
        setScores(d.scores);  // update ArcadeScoreboard too
      }
    });
    s.on("bumper_state",d=>{
      // Normalize server field time_left → timeLeft; always keep latest scores
      bumperStateRef.current={
        ...d,
        timeLeft: d.time_left ?? d.timeLeft ?? bumperStateRef.current.timeLeft,
        scores: d.scores || bumperStateRef.current.scores || {},
      };
    });
    s.on("bumper_player_out",d=>{
      setBumperElims(prev=>[...prev.slice(-3),{player:d.player,by:d.by,ts:Date.now()}]);
    });
    s.on("game_ended",d=>{playSound("game_end");setGameState("finished");setFinalScores(d.final_scores);setWinner(d.winner);setGameEndData(d);});

    // Quiz
    s.on("quiz_question",d=>{
      const restoredAnswered=d.answered_players||[];
      if(d.logo){setCarsLogoSlug(d.logo);setCarsOpts(d.options);setCarsResult(null);setCurRound(d.round);setMaxRounds(d.max_rounds);setAnswered(restoredAnswered);return;}
      setQuizQ(d.question);setQuizOpts(d.options);setCurRound(d.round);setMaxRounds(d.max_rounds);setAnswered(restoredAnswered);setQuizResult(null);speak(d.question,gameTypeRef.current);});
    s.on("player_answered",d=>setAnswered(p=>[...p,d.player_name]));
    s.on("quiz_result",d=>{playSound("round_end");setQuizResult(d);setCarsResult(d);setScores(d.scores);});

    // Drawing
    s.on("drawing_start",d=>{setDrawer(d.drawer);setDrawHint(d.hint);setCurRound(d.round);setMaxRounds(d.max_rounds);setDrawData(null);setGuesses([]);setRevealed(null);});
    s.on("drawing_update",d=>setDrawData(d.drawing));
    s.on("correct_guess",d=>{playSound("correct");setGuesses(p=>[...p,{player:d.player_name,correct:true}]);setScores(d.scores);toast.success(`${d.player_name} угадал!`);});
    s.on("wrong_guess",d=>setGuesses(p=>[...p,{player:d.player_name,guess:d.guess,correct:false}]));
    s.on("drawing_end",d=>{setRevealed(d.word);setScores(d.scores);});

    // Voting
    s.on("voting_question",d=>{setVotingQ(d.question);setCurRound(d.round);setMaxRounds(d.max_rounds);setVoted(d.voted_players||[]);setVResult(null);speak(d.question,"voting");});
    s.on("player_voted",d=>setVoted(p=>[...p,d.player_name]));
    s.on("voting_result",d=>{playSound("round_end");setVResult(d);setScores(d.scores);});

    // Truth or Dare
    s.on("truthdare_turn",d=>{setTdPlayer(d.current_player);setCurRound(d.round);setMaxRounds(d.max_rounds);setTdType(null);setTdContent(null);setTdDone(false);setTdSkipped(false);});
    s.on("truthdare_question",d=>{playSound(d.type==="truth"?"truth":"dare");setTdType(d.type);setTdContent(d.type==="truth"?d.question:d.dare);speak(d.type==="truth"?d.question:d.dare,"truthdare");});
    s.on("truthdare_completed",d=>{
      setScores(d.scores);
      if(d.skipped){
        setTdSkipped(true);
        toast.error(`${d.player} отказался (−25 очков)`);
      } else {
        setTdDone(true);
        toast.success(`${d.player} выполнил! +50 очков`);
      }
    });

    // Crocodile
    s.on("crocodile_start",d=>{setCExplainer(d.explainer);setCWord(d.word||"");setCCat(d.category||"");setCTime(d.time_seconds);setCMaxTime(d.time_seconds);setCGuessedBy([]);setCGuesses([]);setCEnd(null);setCurRound(d.round);setMaxRounds(d.max_rounds);});
    s.on("crocodile_tick",d=>setCTime(d.remaining));
    s.on("crocodile_correct",d=>{playSound("correct");setCGuessedBy(p=>[...p,d.player_name]);setCGuesses(p=>[...p,{player:d.player_name,correct:true}]);setScores(d.scores);toast.success(`🐊 ${d.player_name} угадал!`);});
    s.on("crocodile_wrong",d=>setCGuesses(p=>[...p,{player:d.player_name,guess:d.guess,correct:false}]));
    s.on("crocodile_end",d=>{playSound(d.time_expired?"time_up":"round_end");setCEnd(d);setScores(d.scores);});

    // Music (обычный / реверс / быстрый — один обработчик)
    const onMusicQuestion = d => {
      musicRetryRef.current = 0;
      setMusicOpts(d.options); setMusicAnswered(d.answered_players||[]); setMusicResult(null);
      setMusicTime(d.time_seconds||30); setMusicHint(d.hint||"");
      setMusicAudioUrl(d.audio_url||""); setMusicCoverUrl(d.cover_url||"");
      setReverseProxyUrl(d.proxy_url||""); setPlaybackRate(d.playback_rate||1);
      setCurRound(d.round); setMaxRounds(d.max_rounds);
    };
    s.on("music_question", onMusicQuestion);
    s.on("reverse_music_question", onMusicQuestion);
    s.on("fast_music_question", onMusicQuestion);
    s.on("puzzle_music_question", d => {
      musicRetryRef.current = 0;
      setMusicOpts(d.options); setMusicAnswered(d.answered_players||[]); setMusicResult(null);
      setMusicTime(d.time_seconds||30); setMusicHint(d.hint||"");
      setMusicAudioUrl(d.audio_url||""); setMusicCoverUrl(d.cover_url||"");
      setPuzzleSegments(d.puzzle_segments||[]);
      setCurRound(d.round); setMaxRounds(d.max_rounds);
    });
    s.on("music_tick",d=>setMusicTime(d.remaining));
    s.on("music_player_answered",d=>setMusicAnswered(p=>[...p,d.player_name]));
    s.on("music_result",d=>{playSound("round_end");setMusicResult(d);setScores(d.scores);});
    s.on("emoji_movie_question",d=>{
      setEmojiQ(d);setEmojiAnswered(d.answered_players||[]);setEmojiResult(null);
      setEmojiTime(d.time_seconds||20);
      setCurRound(d.round);setMaxRounds(d.max_rounds);
    });
    s.on("emoji_movie_tick",d=>{setEmojiTime(d.remaining);});
    s.on("emoji_movie_player_answered",d=>{setEmojiAnswered(a=>[...a,d.player_name]);});
    s.on("emoji_movie_result",d=>{playSound("round_end");setEmojiResult(d);setScores(d.scores);});

    s.on("fact_or_fake_question",d=>{
      setFofFact(d.fact);setFofAnswered(d.answered_players||[]);setFofResult(null);
      setFofTime(d.time_seconds||15);
      setCurRound(d.round);setMaxRounds(d.max_rounds);
      speak(d.fact,"fact_or_fake");
    });
    s.on("fact_or_fake_tick",d=>{setFofTime(d.remaining);});
    s.on("fact_or_fake_player_answered",d=>{setFofAnswered(a=>[...a,d.player_name]);});
    s.on("fact_or_fake_result",d=>{playSound("round_end");setFofResult(d);setScores(d.scores);});

    // ── Flappy Bird ───────────────────────────────────────────────────────────
    s.on("flappy_question", d => {
      setCurRound(d.round); setMaxRounds(d.max_rounds);
      setFlappyData(d);
      setFlappyRoundEnd(null);
      setFlappyAlive(new Set(d.players.map(p => p.name)));
      setFlappyScores({});
      setFlappyPositions({});
    });
    s.on("flappy_pos", d => {
      // Позиция птички от игрока — обновляем без лишних ре-рендеров
      setFlappyPositions(prev => ({...prev, [d.name]: {x:d.x,y:d.y,vy:d.vy,pipes:d.pipes,alive:d.alive,frame:d.frame,pipeData:d.pipeData||[]}}));
    });
    s.on("flappy_died", d => {
      setFlappyAlive(a => { const s2 = new Set(a); s2.delete(d.name); return s2; });
      setFlappyScores(sc => ({...sc, [d.name]: d.score}));
      setFlappyPositions(prev => ({...prev, [d.name]: {...(prev[d.name]||{}), alive:false}}));
    });
    s.on("flappy_round_end", d => {
      setFlappyRoundEnd(d);
      setFlappyScores(d.scores || {});
      if(d.total) setScores(d.total);
    });

    // ── Диктатор ────────────────────────────────────────────────────────────
    s.on("dictator_question", d => {
      setCurRound(d.round); setMaxRounds(d.max_rounds);
      setDictData(d); setDictLaw(null); setDictVoteData(null); setDictResult(null);
      if(d.scores) setScores(d.scores);
    });
    s.on("dictator_law_set", d => {
      setDictLaw(d);
      setDictData(prev => prev ? {...prev, phase:"discussion"} : prev);
    });
    s.on("dictator_vote_start", d => {
      setDictVoteData(d);
      setDictData(prev => prev ? {...prev, phase:"vote"} : prev);
    });
    s.on("dictator_result", d => {
      setDictResult(d);
      setDictData(prev => prev ? {...prev, phase:"result"} : prev);
      if(d.scores) setScores(d.scores);
    });

    // ── 2 правды 1 ложь ─────────────────────────────────────────────────────
    s.on("two_truths_question", d => {
      setCurRound(d.round); setMaxRounds(d.max_rounds);
      setTtData(d); setTtVoteData(null); setTtVoteProgress(null); setTtResult(null);
      if(d.scores) setScores(d.scores);
    });
    s.on("two_truths_vote_start", d => {
      setTtVoteData(d);
      setTtData(prev => prev ? {...prev, phase:"voting"} : prev);
      setTtVoteProgress({voted_count:d.voted_count||0, total: d.total || 0});
    });
    s.on("two_truths_vote_update", d => {
      setTtVoteProgress(d);
    });
    s.on("two_truths_result", d => {
      setTtResult(d);
      setTtData(prev => prev ? {...prev, phase:"result"} : prev);
      if(d.scores) setScores(d.scores);
    });

    // ── Stroop ──────────────────────────────────────────────────────────────
    s.on("stroop_question", d => {
      setStroopQuestion(d);
      setStroopResult(null);
      setStroopLives(d.lives || {});
      setCurRound(d.qnum);
      setMaxRounds(d.total);
      if(d.scores) setScores(d.scores);
    });
    s.on("stroop_result", d => {
      setStroopResult(d);
      setStroopLives(d.lives || {});
      if(d.scores) setScores(d.scores);
    });

    // ── Wheel of Words ──────────────────────────────────────────────────────
    s.on("wheelword_question",d=>{
      setCurRound(d.round);setMaxRounds(d.max_rounds);
      setWwWord(d);setWwCurrentPlayer(d.current_player);setWwPhase(d.phase||"spin");
      setWwSpinResult(null);setWwLetterResult(null);setWwWordResult(null);
      setWwHint(null);setWwRoundEnd(null);setWwTick(60);
      setWwGuessedLetters(d.guessed_letters||[]);
      setWwRoundScores(d.round_scores||{});
      setWwEliminated(d.eliminated||[]);
    });
    s.on("wheelword_turn",d=>{
      setWwCurrentPlayer(d.current_player);setWwPhase(d.phase||"spin");
      setWwSpinResult(null);setWwLetterResult(null);setWwWordResult(null);setWwHint(null);
      if(d.word_mask)setWwWord(w=>({...w,word_mask:d.word_mask}));
      setWwGuessedLetters(d.guessed_letters||[]);
      setWwRoundScores(d.round_scores||{});
      setWwEliminated(d.eliminated||[]);
      setWwTick(60);
    });
    s.on("wheelword_tick",d=>{setWwTick(d.remaining);});
    s.on("wheelword_spin_result",d=>{
      // Только sector_idx для анимации — результат придёт в spin_reveal
      setWwSpinResult(d);
      setWwSpinResultVisible(false);
      setWwSpinKey(k=>k+1); // триггерим анимацию колеса
    });
    s.on("wheelword_spin_reveal",d=>{
      // Колесо остановилось — теперь показываем результат
      setWwSpinResult(d); // обновляем данные (label/type) но НЕ меняем spinKey
      setWwSpinResultVisible(true);
      setWwPhase("action");
    });
    s.on("wheelword_letter_result",d=>{
      setWwLetterResult(d);
      if(d.word_mask)setWwWord(w=>({...w,word_mask:d.word_mask}));
      setWwGuessedLetters(d.guessed_letters||[]);
      setWwRoundScores(d.round_scores||{});
    });
    s.on("wheelword_word_result",d=>{setWwWordResult(d);});
    s.on("wheelword_hint_used",d=>{
      setWwHint(d);
      if(d.word_mask)setWwWord(w=>({...w,word_mask:d.word_mask}));
      setWwGuessedLetters(d.guessed_letters||[]);
      setWwRoundScores(d.round_scores||{});
    });
    s.on("wheelword_round_end",d=>{
      setWwRoundEnd(d);
    });

    s.on("sniper_turn",d=>{
      setSniperTurn(d); setSniperShot(null);
    });
    s.on("sniper_shot_result",d=>{
      setSniperShot(d);
      if(d.scores) setScores(d.scores);
    });
    s.on("simon_update",d=>{
      setSimonData(d);
      if(d.scores) setScores(d.scores);
    });
    s.on("quest_update",d=>{
      setQuestData(d);
      if(d.scores) setScores(d.scores);
    });
    s.on("quest_votes_update",d=>{
      setQuestData(prev => prev ? {
        ...prev,
        vote_counts: d.vote_counts,
        total_voted: d.total_voted,
        total_players: d.total_players,
      } : prev);
    });
    // ── Jeopardy ────────────────────────────────────────────────────────────
    s.on("jeopardy_board",d=>{
      setJeopardyBoard(d);
      if(d.phase === "question" || d.phase === "pick") {
        setJeopardyResult(null);
        setJeopardyTimeout(null);
      }
      if(d.scores) setScores(d.scores);
    });
    s.on("jeopardy_result",d=>{
      setJeopardyResult(d);
      if(d.scores) setScores(d.scores);
      setTimeout(()=>setJeopardyResult(null),3500);
    });
    s.on("jeopardy_timeout",d=>{
      setJeopardyTimeout(d);
      setTimeout(()=>setJeopardyTimeout(null),3500);
    });
    // ── Reaction ────────────────────────────────────────────────────────────
    s.on("reaction_waiting",d=>{
      setReactionPhase("waiting"); setReactionSignal(null); setReactionRoundEnd(null); setReactionEarly(null);
      setReactionRound(d.round); setReactionMaxRounds(d.max_rounds);
      if(d.scores) setScores(d.scores);
    });
    s.on("reaction_signal",d=>{
      setReactionPhase("signal"); setReactionSignal(d);
    });
    s.on("reaction_round_end",d=>{
      setReactionPhase("result"); setReactionRoundEnd(d); setReactionSignal(null);
      if(d.scores) setScores(d.scores);
    });
    s.on("reaction_early",d=>{
      setReactionEarly(d);
      if(d.scores) setScores(d.scores);
      setTimeout(()=>setReactionEarly(null), 2000);
    });


    s.on("taprace_start",d=>{
      setTapraceData({track:d.track,players:d.players,finish_pos:d.finish_pos,timeout:d.timeout,round:d.round,max_rounds:d.max_rounds});
      setCurRound(d.round);setMaxRounds(d.max_rounds);
      setTapracePositions(d.positions||{});setTapraceFinished(d.finished||[]);
      setTapraceComboLevels({});setTapraceMultipliers({});setTapraceRoundResult(null);setTapraceEvent(null);
    });
    s.on("taprace_state",d=>{
      setTapracePositions({...d.positions});
      setTapraceFinished([...d.finished]);
      setTapraceComboLevels({...d.combo_levels});
      setTapraceMultipliers({...d.multipliers});
      if(d.events&&d.events.length>0){setTapraceEvent(d.events[d.events.length-1]);}
    });
    s.on("taprace_player_finished",d=>{
      playSound("round_end");
    });
    s.on("taprace_event",d=>{setTapraceEvent(d);});
    s.on("taprace_round_end",d=>{
      playSound("round_end");
      setTapraceRoundResult(d);setScores(d.scores);setTapraceData(null);
    });
    s.on("quiplash_prompt",d=>{setQuiplashPrompt(d.prompt);setCurRound(d.round);setMaxRounds(d.max_rounds);setQuiplashAnswerCount(d.answer_count||0);setQuiplashAnswerTotal(d.total||0);setQuiplashVoteData(null);setQuiplashResult(null);speak(d.prompt,"quiplash");});
    s.on("quiplash_answered",d=>{setQuiplashAnswerCount(d.answer_count);setQuiplashAnswerTotal(d.total);});
    s.on("quiplash_vote",d=>{setQuiplashVoteData({prompt:d.prompt,answers:d.answers});setQuiplashVoteCount(d.vote_count||0);setQuiplashVoteTotal(d.voter_total??Object.keys(d.answers||{}).length);});
    s.on("quiplash_voted",d=>{setQuiplashVoteCount(d.vote_count);setQuiplashVoteTotal(d.total);});
    s.on("quiplash_vote_result",d=>{playSound("round_end");setQuiplashResult(d);setScores(d.scores);setQuiplashVoteData(null);});
    // Fibbage listeners
    s.on("fibbage_question",d=>{setFibbageQuestion({prompt:d.prompt});setCurRound(d.round);setMaxRounds(d.max_rounds);setFibbageLieCount(d.lie_count||0);setFibbageLieTotal(d.total||0);setFibbageVoteData(null);setFibbageResult(null);speak(d.prompt,"fibbage");});
    s.on("screenplay_phase",d=>{setSpPhase(d);setSpVoteCount(d.vote_count||0);setSpResult(null);setSpFinal(null);speak(d.question,"screenplay");});
    s.on("screenplay_voted",d=>{setSpVoteCount(d.vote_count);});
    s.on("screenplay_phase_result",d=>{setSpResult(d);setScores(d.scores);});
    s.on("screenplay_final",d=>{setSpFinal(d);setScores(d.scores);speak(d.story_text,"screenplay");});

    // Spyfall
    s.on("spyfall_phase",d=>{setSfPhase(d);if(d.phase==="vote")setSfVoteData(null);setSfResult(null);if(d.question)speak(d.question,"spyfall");});
    s.on("spyfall_vote_update",d=>{setSfVoteData(d);});
    s.on("spyfall_round_result",d=>{setSfResult(d);setScores(d.scores);setCurRound(d.round);setMaxRounds(d.max_rounds);});
    s.on("fibbage_lie_submitted",d=>{setFibbageLieCount(d.lie_count);setFibbageLieTotal(d.total);});
    s.on("fibbage_vote",d=>{setFibbageVoteData({prompt:d.prompt,options:d.options});setFibbageVoteCount(d.vote_count||0);setFibbageVoteTotal(d.total||0);});
    s.on("fibbage_voted",d=>{setFibbageVoteCount(d.vote_count);setFibbageVoteTotal(d.total);});
    s.on("fibbage_reveal",d=>{playSound("round_end");setFibbageResult(d);setScores(d.scores);setFibbageVoteData(null);});

    s.on("error",d=>{
      const message=d?.message||"Ошибка подключения";
      toast.error(message);
      if(message.includes("Room not found")||message.includes("Комната не найдена")||message.includes("Недействительный токен хоста")){
        sessionStorage.removeItem(`host_token_${roomId}`);
        navigate("/games");
      }
    });

    return()=>{
      clearInterval(heartbeatInterval);
      // A component unmount may be a refresh/reconnect. Let Socket.IO disconnect
      // normally and let the server grace period decide whether the room is dead.
      // Explicit UI exit buttons still emit host_leave immediately.
      s.disconnect();
      socketRef.current=null;
    };
  },[roomId]);

  const emit=(event,data)=>{ if(socketRef.current) socketRef.current.emit(event,{room_id:roomId,...data}); };

  // Explicit exit should reach the server before React unmounts and closes the
  // socket.  Navigating immediately after emit() can race with disconnect and
  // leave players waiting for the 15s host grace period.
  const leaveHostRoom = useCallback(() => {
    const s = socketRef.current;
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (s?.connected) s.disconnect();
      navigate("/games");
    };
    if (s?.connected) {
      s.emit("host_leave", { room_id: roomId }, finish);
      window.setTimeout(finish, 700);
    } else {
      finish();
    }
  }, [navigate, roomId]);

  // Music: retry when audio fails to load (max 5 attempts per round)
  const musicRetryRef = useRef(0);
  const handleMusicAudioError = useCallback(() => {
    musicRetryRef.current += 1;
    if (musicRetryRef.current > 5) {
      console.warn("[Music] Max retries reached, giving up on this round");
      musicRetryRef.current = 0;
      return;
    }
    console.warn(`[Music] Audio error, retrying (attempt ${musicRetryRef.current}/5)...`);
    if (socketRef.current) {
      socketRef.current.emit("music_skip_track", {
        room_id: roomId,
        game_type: gameTypeRef.current,
        failed_song: musicOpts?.[0] || "",
      });
    }
  }, [roomId, musicOpts]);

  const startGame=()=>{
    stopMusic();
    initAudio();
    const min=MIN_PLAYERS[gameType]||1;
    const connected=players.filter(p=>p.connected!==false).length;
    if(connected<min){
      const word=min===1?"игрок":min<5?"игрока":"игроков";
      toast.error(`Для «${GNAMES[gameType]}» нужно минимум ${min} ${word}`);
      return;
    }
    playSound("click");
    log.game("start_game", roomId, gameType, {players: connected});
    emit("start_game");
  };

  const GameIcon=GICONS[gameType]||Brain;
  const gameColor=GCOLORS[gameType]||"#FF00FF";
  const minRequired=MIN_PLAYERS[gameType]||1;
  const connectedCount=players.filter(p=>p.connected!==false).length;
  const canStart=connectedCount>=minRequired;
  const p=tv?"px-16 py-6":"px-4 py-3"; // header padding

  return(
    <div className="min-h-screen bg-cartoon stars-bg relative" style={{display:"flex",flexDirection:"column"}}>
      {/* ── Header ── */}
      <header className={`relative z-10 flex items-center gap-3 ${p}`}
        style={{borderBottom:"3px solid #000",background:"rgba(0,0,0,0.5)",minHeight:0}}>

        {/* Left: game name + round */}
        <div className="flex items-center gap-2 shrink-0">
          <div style={{background:gameColor,border:"2.5px solid #000",borderRadius:"0.75rem",
            boxShadow:"3px 3px 0 #000",width:tv?"3.5rem":"2.25rem",height:tv?"3.5rem":"2.25rem",
            display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <GameIcon style={{color:"#000",width:tv?"1.9rem":"1.2rem",height:tv?"1.9rem":"1.2rem"}}/>
          </div>
          <div style={{lineHeight:1.2}}>
            <div className="font-heading text-white font-bold" style={{fontSize:tv?"1.5rem":"0.95rem",whiteSpace:"nowrap"}}>{GNAMES[gameType]}</div>
            {gameState==="playing"&&gameType!=="screenplay"&&gameType!=="spyfall"&&<div className="font-body text-[#aaa]" style={{fontSize:tv?"1rem":"0.7rem"}}>Раунд {curRound}/{maxRounds}</div>}
          </div>
        </div>

        {/* Center: room code — FIX: smaller font so it doesn't overflow */}
        <div className="flex-1 text-center" style={{overflow:"hidden"}}>
          <div className="font-body text-[#777] uppercase tracking-widest" style={{fontSize:tv?"0.8rem":"0.55rem",marginBottom:"0.1rem"}}>КОД КОМНАТЫ</div>
          <div className="font-display" data-testid="room-id"
            style={{color:"#FFE000",letterSpacing:"0.25em",textShadow:"2px 2px 0 #000",
              fontSize:tv?"3.5rem":"1.6rem", // FIX: was 5rem/7rem — way too big
              lineHeight:1,WebkitTextStroke:"1px #000"}}>
            {roomId}
          </div>
        </div>

        {/* Right: back button + sound controls + player count */}
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={leaveHostRoom}
            style={{height:tv?"3rem":"2rem",padding:tv?"0 1rem":"0 0.6rem",borderRadius:"0.6rem",
              border:"2px solid #000",boxShadow:"2px 2px 0 #000",background:"rgba(0,0,0,0.6)",
              color:"#aaa",cursor:"pointer",display:"flex",alignItems:"center",gap:"0.35rem",
              fontFamily:"'Fredoka One',sans-serif",fontSize:tv?"1rem":"0.75rem",flexShrink:0}}>
            ← Главная
          </button>
          {[
            {on:musicEnabled,On:MusicOn,Off:MusicOff,col:"#FF00FF",fn:()=>{initAudio();toggleMusic()}},
            {on:soundEnabled,On:SoundOn,Off:SoundOff,col:"#00FFFF",fn:()=>{initAudio();toggleSound()}},
          ].map((b,i)=>{
            const Ic=b.on?b.On:b.Off;
            const sz=tv?26:18;
            return(
              <button key={i} onClick={b.fn} style={{width:tv?"3rem":"2rem",height:tv?"3rem":"2rem",
                borderRadius:"0.6rem",border:"2px solid #000",boxShadow:"2px 2px 0 #000",
                background:"rgba(0,0,0,0.6)",color:b.on?b.col:"#444",cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                <Ic sz={sz}/>
              </button>
            );
          })}
          <div style={{display:"flex",alignItems:"center",gap:"0.35rem",background:"#000",
            border:"2px solid "+gameColor,borderRadius:"0.75rem",boxShadow:"2px 2px 0 #000",
            padding:tv?"0.4rem 1rem":"0.25rem 0.6rem",flexShrink:0}}>
            <Users style={{color:gameColor,width:tv?"1.4rem":"1rem",height:tv?"1.4rem":"1rem"}}/>
            <span className="font-display" style={{fontSize:tv?"1.5rem":"0.95rem",color:"#fff"}}>{players.length}/{MAX_PLAYERS[gameType]||8}</span>
          </div>
        </div>
      </header>

      {/* ── Main content ── */}
      <main className={`relative z-10 flex-1 flex flex-col items-center justify-center ${tv?"px-16 py-8":"px-4 py-4"}`} style={{minHeight:0,overflowY:"auto",overflowX:"hidden"}}>
        {gameState==="waiting"&&(
          <WaitingScreen players={players} onStart={startGame} roomId={roomId}
            tv={tv} gameColor={gameColor} gameType={gameType}
            canStart={canStart} minRequired={minRequired} playerCount={connectedCount}
            playlists={playlists} selectedPlaylist={selectedPlaylist}
            onPlaylistChange={(id)=>{
              setSelectedPlaylist(id);
              socketRef.current?.emit("set_music_playlist",{room_id:roomId,playlist_id:id});
            }}
          />
        )}
        {gameState==="playing"&&["quiz","quiz_cinema","quiz_18","quiz_movies","lexicon"].includes(gameType)&&(
          <QuizHost question={quizQ} options={quizOpts} answered={answered} players={players} result={quizResult} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="cars"&&(
          <CarsHost question={carsLogoSlug} logoSlug={carsLogoSlug} options={carsOpts} answered={answered} players={players} result={carsResult} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="drawing"&&(
          <DrawingHost drawer={drawer} hint={drawHint} drawData={drawData} guesses={guesses}
            revealed={revealed} players={players} onEnd={()=>emit("end_drawing_turn")} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="voting"&&(
          <VotingHost question={votingQ} voted={voted} players={players} result={vResult} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="truthdare"&&(
          <TruthDareHost player={tdPlayer} type={tdType} content={tdContent} done={tdDone} skipped={tdSkipped} players={players} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="crocodile"&&(
          <CrocodileHost explainer={cExplainer} word={cWord} cat={cCat}
            timeLeft={cTime} maxTime={cMaxTime} guessedBy={cGuessedBy} guesses={cGuesses}
            roundEnd={cEnd} players={players} onSkip={()=>emit("crocodile_skip")} tv={tv}/>
        )}
        {gameState==="playing"&&gameType==="emoji_movie"&&(
          <EmojiMovieHost
            q={emojiQ} answered={emojiAnswered} result={emojiResult}
            timeLeft={emojiTime} players={players} tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="fact_or_fake"&&(
          <FactOrFakeHost
            fact={fofFact} answered={fofAnswered} result={fofResult}
            timeLeft={fofTime} players={players} tv={tv}
          />
        )}
        {(gameState==="playing"||flappyRoundEnd)&&gameType==="flappy"&&(
          <FlappyHost
            data={flappyData}
            roundEnd={flappyRoundEnd}
            alive={flappyAlive}
            scores={flappyScores}
            positions={flappyPositionsRef}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="dictator"&&dictData&&(
          <DictatorHost
            data={dictData}
            law={dictLaw}
            voteData={dictVoteData}
            result={dictResult}
            players={players}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="two_truths"&&ttData&&(
          <TwoTruthsHost
            data={ttData}
            voteData={ttVoteData}
            voteProgress={ttVoteProgress}
            result={ttResult}
            players={players}
            tv={tv}
            onSkipInput={() => socketRef.current?.emit("two_truths_skip_input", { room_id: roomId })}
          />
        )}
        {gameState==="playing"&&gameType==="stroop"&&(
          <StroopHost
            question={stroopResult ? null : stroopQuestion}
            result={stroopResult}
            players={players}
            scores={scores}
            lives={stroopLives}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="wheelword"&&(
          <WheelWordHost
            wwWord={wwWord}
            currentPlayer={wwCurrentPlayer}
            phase={wwPhase}
            spinResult={wwSpinResult}
            spinKey={wwSpinKey}
            spinResultVisible={wwSpinResultVisible}
            letterResult={wwLetterResult}
            wordResult={wwWordResult}
            hint={wwHint}
            roundEnd={wwRoundEnd}
            tick={wwTick}
            guessedLetters={wwGuessedLetters}
            roundScores={wwRoundScores}
            eliminated={wwEliminated}
            players={players}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="sniper"&&(
          <SniperHost
            turnData={sniperTurn}
            shotResult={sniperShot}
            players={players}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="simon"&&(
          <SimonHost
            simonData={simonData}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="quest"&&(
          <QuestHost
            questData={questData}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="jeopardy"&&(
          !jeopardyBoard
            ? <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:16,padding:"2rem"}}>
                <div style={{fontFamily:"'Fredoka One',sans-serif",color:"#ffe600",fontSize:"1.5rem"}}>🏆 Своя игра</div>
                <div style={{fontFamily:"'Nunito',sans-serif",color:"#aaa",fontSize:"1rem"}}>Загружаем доску...</div>
                <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{borderColor:"#9b5de5",borderTopColor:"transparent"}}/>
              </div>
            : <JeopardyHost
                board={jeopardyBoard}
                result={jeopardyResult}
                timeout={jeopardyTimeout}
                tv={tv}
                onReveal={()=>socketRef.current?.emit("jeopardy_reveal",{room_id:roomId})}
              />
        )}
        {gameState==="playing"&&gameType==="reaction"&&(
          <ReactionHost
            phase={reactionPhase}
            signal={reactionSignal}
            roundEnd={reactionRoundEnd}
            early={reactionEarly}
            scores={scores}
            players={players}
            round={reactionRound}
            maxRounds={reactionMaxRounds}
            tv={tv}
          />
        )}
                {(gameState==="playing"||tapraceData||tapraceRoundResult)&&gameType==="taprace"&&(
          <TapraceHost
            data={tapraceData}
            positions={tapracePositions}
            finished={tapraceFinished}
            comboLevels={tapraceComboLevels}
            multipliers={tapraceMultipliers}
            roundResult={tapraceRoundResult}
            lastEvent={tapraceEvent}
            players={players}
            scores={scores}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="quiplash"&&(
          <QuiplashHost
            prompt={quiplashPrompt}
            answerCount={quiplashAnswerCount}
            answerTotal={quiplashAnswerTotal}
            voteData={quiplashVoteData}
            voteCount={quiplashVoteCount}
            voteTotal={quiplashVoteTotal}
            result={quiplashResult}
            players={players}
            tv={tv}
            onSkip={() => socketRef.current?.emit("quiplash_force_vote", { room_id: roomId })}
          />
        )}
        {gameState==="playing"&&gameType==="screenplay"&&(
          <ScreenplayHost
            phase={spPhase}
            voteCount={spVoteCount}
            result={spResult}
            final={spFinal}
            players={players}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="spyfall"&&(
          <SpyfallHost
            phase={sfPhase}
            voteData={sfVoteData}
            result={sfResult}
            players={players}
            scores={scores}
            tv={tv}
          />
        )}
        {gameState==="playing"&&gameType==="fibbage"&&(
          <FibbageHost
            question={fibbageQuestion}
            lieCount={fibbageLieCount}
            lieTotal={fibbageLieTotal}
            voteData={fibbageVoteData}
            voteCount={fibbageVoteCount}
            voteTotal={fibbageVoteTotal}
            result={fibbageResult}
            players={players}
            tv={tv}
          />
        )}
        {gameState==="playing"&&(gameType==="music"||gameType==="reverse_music"||gameType==="fast_music")&&(
          <div style={{width:"100%",display:"flex",flexDirection:"column",gap:"0.75rem"}}>
            <MusicHost options={musicOpts} answered={musicAnswered} result={musicResult}
              timeLeft={musicTime} hint={musicHint} audioUrl={musicAudioUrl} coverUrl={musicCoverUrl}
              reverseProxyUrl={reverseProxyUrl} isReverse={gameType==="reverse_music"} playbackRate={gameType==="fast_music"?playbackRate:1}
              players={players} tv={tv} httpUrl={HTTP_URL} onAudioError={handleMusicAudioError}/>
            {musicOpts.length>0&&(
              <div style={{textAlign:"center"}}>
                <button className="btn-cartoon" style={{background:"#333",fontSize:tv?"1rem":"0.8rem",padding:tv?"0.6rem 2rem":"0.4rem 1.25rem"}}
                  onClick={()=>socketRef.current?.emit("music_skip_track",{room_id:roomId,game_type:gameType,failed_song:""})}>
                  ⏭ {musicResult ? "Следующий раунд →" : "Пропустить раунд"}
                </button>
              </div>
            )}
          </div>
        )}
        {gameState==="playing"&&gameType==="puzzle_music"&&(
          <div style={{width:"100%",display:"flex",flexDirection:"column",gap:"0.75rem"}}>
            <PuzzleMusicHost options={musicOpts} answered={musicAnswered} result={musicResult}
              timeLeft={musicTime} hint={musicHint} audioUrl={musicAudioUrl} coverUrl={musicCoverUrl}
              puzzleSegments={puzzleSegments} players={players} tv={tv}/>
            {musicOpts.length>0&&(
              <div style={{textAlign:"center"}}>
                <button className="btn-cartoon" style={{background:"#333",fontSize:tv?"1rem":"0.8rem",padding:tv?"0.6rem 2rem":"0.4rem 1.25rem"}}
                  onClick={()=>socketRef.current?.emit("music_skip_track",{room_id:roomId,game_type:"puzzle_music",failed_song:""})}>
                  ⏭ {musicResult ? "Следующий раунд →" : "Пропустить раунд"}
                </button>
              </div>
            )}
          </div>
        )}
        {gameState==="playing"&&gameType==="bumper"&&bumperArena&&(
          <BumperHost
            stateRef={bumperStateRef}
            arena={bumperArena}
            players={players}
            elims={bumperElims}
            roundResult={bumperRoundResult}
            tv={tv}
          />
        )}
        {gameState==="finished"&&(
          <ResultsScreen finalScores={finalScores} winner={winner} gameEndData={gameEndData}
            onRestart={()=>emit("restart_game")} onHome={leaveHostRoom} tv={tv}/>
        )}
      </main>

      {/* ── Arcade scoreboard ── */}
      {gameState==="playing"&&<ArcadeScoreboard players={players} scores={scores} tv={tv}/>}
    </div>
  );
}

// ── WAITING SCREEN ───────────────────────────────────────────────────────────
function WaitingScreen({players,onStart,roomId,tv,gameColor,gameType,canStart,minRequired,playerCount,playlists,selectedPlaylist,onPlaylistChange}){
  const MUSIC_TYPES = new Set(["music","reverse_music","fast_music","puzzle_music"]);
  const isMusicGame = MUSIC_TYPES.has(gameType);
  const url=`${window.location.origin}/join?room=${roomId}`;
  const need=minRequired-(playerCount||players.filter(p=>p.connected!==false).length);
  const needText=need<=0?"":`Ещё нужно: ${need} ${need===1?"игрок":need<5?"игрока":"игроков"}`;

  const chips=players.map((p,i)=>{
    const isConnected=p.connected!==false;
    return(
      <div key={i} className="animate-bounce-in"
        style={{display:"inline-flex",flexDirection:"column",alignItems:"center",
          gap:"0.3rem",animationDelay:`${i*0.08}s`,
          opacity:isConnected?1:0.35,filter:isConnected?"none":"grayscale(0.6)"}}>
        <Crewmate color={p.color} size={tv?56:44} animate={isConnected?"bounce":undefined}/>
        <div style={{
          background:p.color,border:"2.5px solid #000",borderRadius:"9999px",
          boxShadow:"2px 2px 0 #000",
          padding:tv?"0.25rem 0.9rem":"0.2rem 0.65rem",
          fontFamily:"'Fredoka One',sans-serif",
          fontSize:tv?"1rem":"0.78rem",color:"#000",whiteSpace:"nowrap"}}
          data-testid={`player-${p.name}`}>
          {p.name}{!isConnected?" 📵":""}
        </div>
      </div>
    );
  });

  const playlistSelector = isMusicGame && playlists.length > 0 && (
    <div style={{marginBottom:"1.25rem"}}>
      <p style={{
        fontFamily:"'Fredoka One',sans-serif",
        fontSize: tv ? "1.1rem" : "0.85rem",
        color:"#9b5de5", marginBottom:"0.5rem", textAlign:"center",
      }}>
        🎵 Плейлист
      </p>
      <div style={{display:"flex",flexWrap:"wrap",gap:"0.4rem",justifyContent:"center"}}>
        {playlists.map(pl=>{
          const isSelected = selectedPlaylist===pl.id;
          return(
            <button key={pl.id} onClick={()=>onPlaylistChange(pl.id)} style={{
              background: isSelected ? "#9b5de522" : "#ffffff08",
              border:`2px solid ${isSelected?"#9b5de5":"#ffffff15"}`,
              borderRadius:"2rem",
              padding: tv ? "0.5rem 1.1rem" : "0.35rem 0.8rem",
              fontFamily:"'Fredoka One',sans-serif",
              fontSize: tv ? "1rem" : "0.78rem",
              color: isSelected ? "#fff" : "#666",
              cursor:"pointer", transition:"all 0.15s",
              boxShadow: isSelected ? "0 0 12px #9b5de555" : "none",
            }}>
              {pl.title}
            </button>
          );
        })}
      </div>
      {playlists.find(p=>p.id===selectedPlaylist)?.description&&(
        <p style={{
          fontFamily:"'Nunito',sans-serif",
          fontSize: tv?"0.85rem":"0.7rem",
          color:"#444", textAlign:"center", marginTop:"0.3rem",
        }}>
          {playlists.find(p=>p.id===selectedPlaylist).description}
        </p>
      )}
    </div>
  );

  const startBtn=(sizeCls,iconSz)=>(
    <div className="text-center">
      <button onClick={onStart} disabled={!canStart} data-testid="start-game-btn"
        className={`btn-cartoon ${sizeCls} ${canStart?"btn-success":"btn-dark"}`}
        style={{display:"inline-flex",alignItems:"center",gap:"0.75rem"}}>
        <PlayIcon sz={iconSz}/> Начать игру!
      </button>
      {!canStart&&(
        <div className="font-body text-[#888] mt-2" style={{fontSize:tv?"1.25rem":"0.85rem"}}>
          {needText}
        </div>
      )}
    </div>
  );

  if(tv) return(
    <div className="flex items-center justify-between gap-16 w-full animate-slide-up">
      {/* QR block */}
      <div className="flex flex-col items-center gap-5 shrink-0">
        <div style={{background:"#fff",border:"4px solid #000",borderRadius:"1.5rem",boxShadow:"7px 7px 0 #000",padding:"1.25rem"}}>
          <QRCodeSVG value={url} size={260} bgColor="#fff" fgColor="#0f0025" level="M"/>
        </div>
        <div className="text-center">
          <div className="font-body text-[#aaa] text-lg">Зайди на сайт или сканируй QR</div>
          <div className="font-heading text-white text-xl font-bold">{window.location.origin}/join</div>
        </div>
      </div>
      {/* Player list + start */}
      <div className="flex-1 text-center">
        <h2 className="font-display text-5xl text-white uppercase mb-2" style={{textShadow:"4px 4px 0 #000"}}>Ожидание</h2>
        <h2 className="font-display text-5xl uppercase mb-4" style={{color:gameColor,textShadow:"4px 4px 0 #000"}}>игроков</h2>
        {GAME_RULES[gameType]&&(
          <p className="font-heading text-[#aaa] mb-6 max-w-lg mx-auto" style={{fontSize:"1.1rem",lineHeight:1.4}}>
            📋 {GAME_RULES[gameType]}
          </p>
        )}
        <div className="flex flex-wrap justify-center gap-3 mb-10 max-h-52 overflow-hidden">
          {chips}
          {!players.length&&<div className="font-heading text-2xl text-[#444]">Пока никого...</div>}
        </div>
        {playlistSelector}
        {startBtn("text-3xl px-14 py-6",32)}
      </div>
    </div>
  );

  return(
    <div className="text-center max-w-lg mx-auto w-full animate-slide-up">
      <div className="card-cartoon inline-flex flex-col items-center gap-3 mb-5">
        <div className="font-body text-[#aaa] text-xs uppercase tracking-widest">Код комнаты</div>
        <div className="font-display" style={{color:"#FFE000",fontSize:"2.5rem",letterSpacing:"0.3em",textShadow:"2px 2px 0 #000",WebkitTextStroke:"1px #000"}}
          data-testid="waiting-room-id">{roomId}</div>
        <div style={{background:"#fff",border:"3px solid #000",borderRadius:"1rem",boxShadow:"4px 4px 0 #000",padding:"0.75rem"}}>
          <QRCodeSVG value={url} size={160} bgColor="#fff" fgColor="#0f0025" level="M"/>
        </div>
        <div className="font-body text-[#555] text-xs">{window.location.origin}/join</div>
      </div>
      <div className="flex flex-wrap justify-center gap-2 mb-5">
        {chips}
        {!players.length&&<div className="font-heading text-[#444] text-sm">Пока никого...</div>}
      </div>
      {playlistSelector}
      {startBtn("text-xl px-10 py-4",22)}
    </div>
  );
}

// ── VOTING ───────────────────────────────────────────────────────────────────
const SUIT_COLOR = {"♠":"#222","♣":"#222","♥":"#e00","♦":"#e00"};
const UNO_COLOR = {red:"#E53935",yellow:"#FDD835",green:"#43A047",blue:"#1E88E5",wild:"#7B1FA2"};

function ResultsScreen({finalScores,winner,gameEndData,onRestart,onHome,tv}){
  const isTie = gameEndData?.is_tie;
  const winners = gameEndData?.winners || (winner ? [winner] : []);
  const ranked = gameEndData?.ranked;

  // Build place map for tie-aware display
  const placeMap = {};
  if(ranked) { ranked.forEach(r=>{ placeMap[r.name]=r.place; }); }
  else { finalScores.forEach(([name],i)=>{ placeMap[name]=i+1; }); }

  return(
    <div className="text-center mx-auto animate-slide-up" style={{maxWidth:tv?"860px":"560px"}}>
      <div className="flex justify-center mb-2">
        {isTie
          ? <div style={{fontSize:tv?"4rem":"2.5rem"}}>🤝</div>
          : <Crewmate color="#ffe600" size={tv?110:80} animate="bounce" crown/>
        }
      </div>
      {isTie ? (
        <>
          <h2 className="font-display text-white uppercase mb-1" style={{fontSize:tv?"3.5rem":"2rem",textShadow:"4px 4px 0 #000"}}>НИЧЬЯ!</h2>
          <div className="font-display mb-2" style={{fontSize:tv?"2rem":"1.25rem",color:"#ffe600",textShadow:"2px 2px 0 #000"}}>
            {winners.join(" и ")}
          </div>
          <div className="font-heading text-[#aaa] mb-4" style={{fontSize:tv?"1.3rem":"0.85rem"}}>
            Победители делят трофей поровну 🏆
          </div>
        </>
      ) : (
        <>
          <h2 className="font-display text-white uppercase mb-1" style={{fontSize:tv?"3.5rem":"2rem",textShadow:"4px 4px 0 #000"}}>ПОБЕДИТЕЛЬ</h2>
          <div className="font-display mb-5" style={{fontSize:tv?"3.5rem":"2rem",color:"#ffe600",textShadow:"3px 3px 0 #000"}}>{winner}</div>
        </>
      )}
      <div style={{background:"#1e0845",border:"3px solid #000",borderRadius:"1.5rem",boxShadow:"6px 6px 0 #000",
        padding:tv?"1.75rem":"1.1rem",marginBottom:"1.35rem"}}>
        <h3 className="font-heading text-[#aaa] mb-3" style={{fontSize:tv?"1.5rem":"1rem"}}>Финальные очки</h3>
        <div className="space-y-2">
          {finalScores.map(([name,score],i)=>{
            const place = placeMap[name] || (i+1);
            const isTieWinner = isTie && winners.includes(name);
            const trophyColors=["#FFE000","#C0C0C0","#CD7F32"];
            return(
            <div key={name} style={{background: isTieWinner?"#1e0845":"#0f0025",
              border:`2.5px solid ${isTieWinner?"#FFE000":"#000"}`,borderRadius:"1rem",
              boxShadow:"3px 3px 0 #000",padding:tv?"1rem 1.35rem":"0.6rem 1rem",
              display:"flex",alignItems:"center",gap:"0.65rem"}}
              data-testid={`final-score-${name}`}>
              <span className="font-display text-[#aaa]" style={{fontSize:tv?"2rem":"1.25rem",minWidth:"1.5rem"}}>{place}</span>
              {place<=3&&<Trophy style={{color:trophyColors[place-1]||"#888",width:tv?"1.5rem":"1rem",height:tv?"1.5rem":"1rem",flexShrink:0}}/>}
              {isTieWinner&&<span style={{fontSize:tv?"1.5rem":"1rem"}}>🤝</span>}
              <span className="font-heading flex-1 text-left" style={{fontSize:tv?"1.75rem":"0.95rem"}}>{name}</span>
              <span className="font-display" style={{fontSize:tv?"2rem":"1.25rem",color:"#FF00FF",textShadow:"2px 2px 0 #000"}}>{score}</span>
            </div>
          );})}
        </div>
      </div>
      <div className="flex gap-3 justify-center">
        <button onClick={onRestart} data-testid="restart-btn" className="btn-cartoon btn-primary"
          style={{fontSize:tv?"1.5rem":"1rem",padding:tv?"0.9rem 2.25rem":"0.65rem 1.5rem"}}>
          <RotateCcw style={{width:"1rem",height:"1rem",display:"inline",marginRight:"0.35rem"}}/> Ещё раз!
        </button>
        <button onClick={onHome} data-testid="home-btn" className="btn-cartoon btn-secondary"
          style={{fontSize:tv?"1.5rem":"1rem",padding:tv?"0.9rem 2.25rem":"0.65rem 1.5rem"}}>
          На главную
        </button>
      </div>
    </div>
  );
}

// ── ARCADE SCOREBOARD ────────────────────────────────────────────────────────
function ArcadeScoreboard({players,scores,tv}){
  const sorted=[...players].sort((a,b)=>(scores[b.name]||0)-(scores[a.name]||0));
  return(
    <div className="relative z-10 scoreboard">
      {sorted.map((p,i)=>(
        <div key={p.name} className="score-item" style={{borderColor:p.color}} data-testid={`score-${p.name}`}>
          {i===0&&<span style={{fontSize:"0.85rem"}}>👑</span>}
          <Crewmate color={p.color} size={24} crown={i===0}/>
          <span className="font-heading text-white">{p.name}</span>
          <span className="score-num">{scores[p.name]||0}</span>
        </div>
      ))}
    </div>
  );
}